import { supabase, supabaseAdmin } from '../../supabase';
// import { createClient } from '@supabase/supabase-js';
import { Session } from '@supabase/supabase-js';
import { getToken } from '@/utils/auth';
import { apiSlice } from '@/reducers';
import {
  ILoginCredentials,
  IOtpVerificationCredentials,
  IRegisterCredentials,
  IRegisterRequest,
  IAllowedDomainsResponseWrapper,
} from './authServiceTypes';
class AuthService {
  /**
   * Ensure supabaseAdmin is available
   */
  private ensureSupabaseAdmin() {
    if (!supabaseAdmin) {
      throw new Error('Service configuration error.');
    }
    return supabaseAdmin;
  }

  /**
   * Check if user has expired unverified account and clean it up
   */
  private async checkAndCleanExpiredAccount(
    email: string
  ): Promise<{ isExpired: boolean; existsInMongo: boolean }> {
    try {
      const admin = this.ensureSupabaseAdmin();

      // Check MongoDB first
      const mongoResponse = await fetch(`${process.env.REACT_APP_API}/users/emails/${email}`);
      const existsInMongo = mongoResponse.ok;

      // Check Supabase
      const {
        data: { users },
        error,
      } = await admin.auth.admin.listUsers({ page: 1, perPage: 1000 });
      if (error) return { isExpired: false, existsInMongo };

      const supabaseUser = users.find((user) => user.email === email);

      if (supabaseUser && !supabaseUser.email_confirmed_at) {
        const createdAt = new Date(supabaseUser.created_at);
        const hoursDiff = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);

        if (hoursDiff > 24) {
          // Clean up expired account
          try {
            await admin.auth.admin.deleteUser(supabaseUser.id);
            if (existsInMongo) {
              await fetch(`${process.env.REACT_APP_API}/users/emails/${email}`, {
                method: 'DELETE',
                headers: { ...getToken(), 'Content-Type': 'application/json' },
              });
            }
          } catch (cleanupError) {
            console.error('Cleanup error:', cleanupError);
          }
          return { isExpired: true, existsInMongo };
        }
      } else if (existsInMongo && !supabaseUser) {
        console.log('Found MongoDB-only user, checking for cleanup:', email);
        // User exists in MongoDB but not in Supabase - likely expired and cleaned up
        // Only treat as expired during registration flow, not login flow
        try {
          const userDataResponse = await fetch(
            `${process.env.REACT_APP_API}/users/emails/${email}`
          );
          if (!userDataResponse.ok) {
            return { isExpired: false, existsInMongo: false };
          }

          const mongoData = await userDataResponse.json();
          const userData = mongoData?.data;

          // Safety check: only clean up regular users with explicit 'user' role
          if (userData && userData.role === 'user') {
            // User exists only in MongoDB = likely expired
            // Try cleanup but don't depend on it
            try {
              const deleteResponse = await fetch(
                `${process.env.REACT_APP_API}/users/emails/${email}`,
                {
                  method: 'DELETE',
                  headers: { ...getToken(), 'Content-Type': 'application/json' },
                }
              );
              console.log('MongoDB cleanup result:', deleteResponse.status);
            } catch (deleteError) {
              console.error('Failed to cleanup MongoDB user:', deleteError);
            }
            return { isExpired: true, existsInMongo: true };
          }

          // For non-user roles, be conservative
          return { isExpired: false, existsInMongo: true };
        } catch (cleanupError) {
          console.error('MongoDB cleanup error:', cleanupError);
          // If any error, be conservative - don't treat as expired
          return { isExpired: false, existsInMongo: true };
        }
      }

      return { isExpired: false, existsInMongo };
    } catch (error) {
      console.error('Error checking expired account:', error);
      return { isExpired: false, existsInMongo: false };
    }
  }

  /**
   * Get the current session
   */
  async getSession(): Promise<Session | null> {
    const { data } = await supabase.auth.getSession();
    return data.session;
  }

  /**
   * Verify credentials and return user role
   */
  async verifyCredentials(credentials: ILoginCredentials, isAdminPath?: boolean): Promise<string> {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });
      if (error) {
        if (error.message && error.message.includes('Email not confirmed')) {
          if (isAdminPath) {
            try {
              const mongoResponse = await fetch(
                `${process.env.REACT_APP_API}/users/emails/${credentials.email}`
              );
              if (mongoResponse.ok) {
                const mongoData = await mongoResponse.json();
                const userRole = mongoData.data?.role || 'user';
                if (userRole !== 'admin') {
                  throw new Error('Invalid email or password');
                }
              } else {
                throw new Error('Invalid email or password');
              }
            } catch (mongoError) {
              throw new Error('Invalid email or password');
            }
          }

          // For non-admin paths, check if account is expired
          // But first verify the user actually exists in Supabase as unverified
          const admin = this.ensureSupabaseAdmin();
          const {
            data: { users },
            error: userError,
          } = await admin.auth.admin.listUsers({ page: 1, perPage: 1000 });

          if (!userError) {
            const supabaseUser = users.find((user) => user.email === credentials.email);
            if (supabaseUser && !supabaseUser.email_confirmed_at) {
              // User exists and is unverified, check if expired
              const createdAt = new Date(supabaseUser.created_at);
              const hoursDiff = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);

              if (hoursDiff > 24) {
                // Clean up expired account
                try {
                  await admin.auth.admin.deleteUser(supabaseUser.id);
                  // Also try to clean MongoDB
                  await fetch(`${process.env.REACT_APP_API}/users/emails/${credentials.email}`, {
                    method: 'DELETE',
                    headers: { ...getToken(), 'Content-Type': 'application/json' },
                  });
                } catch (cleanupError) {
                  console.error('Cleanup error:', cleanupError);
                }
                throw new Error(
                  "Your account wasn't verified within 24 hours. Please register again."
                );
              }
            }
          }

          throw new Error(
            'The registered email is not verified yet. Please check your inbox and confirm to access your account.'
          );
        }
        if (error.message && error.message.includes('Invalid login credentials')) {
          // Check for expired account first
          const { isExpired, existsInMongo } = await this.checkAndCleanExpiredAccount(
            credentials.email
          );
          if (isExpired) {
            throw new Error("Your account wasn't verified within 24 hours. Please register again.");
          }

          // Check if user exists in Supabase
          const admin = this.ensureSupabaseAdmin();
          const {
            data: { users },
            error: userError,
          } = await admin.auth.admin.listUsers({ page: 1, perPage: 1000 });

          if (!userError) {
            const supabaseUser = users.find((user) => user.email === credentials.email);
            if (supabaseUser) {
              throw new Error('Invalid email or password');
            }
          }

          // If exists in MongoDB but not Supabase, it's likely an expired account
          if (existsInMongo) {
            throw new Error("Your account wasn't verified within 24 hours. Please register again.");
          }

          throw new Error(
            'This email address is not registered. Please enter a valid School District email'
          );
        }
        throw new Error('Invalid email or password');
      }

      const mongoResponse = await fetch(
        `${process.env.REACT_APP_API}/users/emails/${credentials.email}`
      );
      let userRole = 'user';

      if (mongoResponse.ok) {
        const mongoData = await mongoResponse.json();
        userRole = mongoData.data?.role || 'user';
      }

      await supabase.auth.signOut();
      return userRole;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Invalid email or password');
    }
  }

  /**
   * Send OTP to user's email
   */
  async sendLoginOtp(email: string): Promise<void> {
    // Using proxy to avoid CORS issues
    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-login-otp`,
      {
        method: 'POST',
        headers: {
          authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      }
    );

    const otpData = await response.json();

    if (!response.ok) {
      throw new Error(otpData.error || 'Failed to send Secure Code');
    }
  }

  /**
   * Verify OTP and sign in
   */
  async verifyOtpAndSignIn(credentials: IOtpVerificationCredentials): Promise<Session> {
    // Using proxy to avoid CORS issues
    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/verify-login-otp`,
      {
        method: 'POST',
        headers: {
          authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
          otp: credentials.otp,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || `Error: ${response.status}`);
    }

    if (data.session) {
      await supabase.auth.setSession(data.session);
      return data.session;
    }

    throw new Error('No session returned from server');
  }

  /**
   * Register a new user
   */
  async registerUser(credentials: IRegisterCredentials): Promise<void> {
    // Check for expired account and clean up if needed
    const { isExpired, existsInMongo } = await this.checkAndCleanExpiredAccount(credentials.email);
    console.log('Registration check result:', {
      isExpired,
      existsInMongo,
      email: credentials.email,
    });
    if (isExpired) {
      // Account was expired and cleaned up, wait briefly before proceeding
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } else if (existsInMongo) {
      // Check Supabase verification status
      const admin = this.ensureSupabaseAdmin();
      const {
        data: { users },
        error: userError,
      } = await admin.auth.admin.listUsers({ page: 1, perPage: 1000 });

      if (!userError) {
        const supabaseUser = users.find((user) => user.email === credentials.email);

        if (supabaseUser?.email_confirmed_at) {
          throw new Error(
            'An account with this email already exists. Login or Reset Password if you forgot your credentials.'
          );
        }

        if (supabaseUser && !supabaseUser.email_confirmed_at) {
          throw new Error(
            'This email is already registered but not verified. Please check your inbox to confirm your account.'
          );
        }
      }

      // User in MongoDB but not in Supabase (likely expired)
      throw new Error(
        'This email is already registered but not verified. Please check your inbox to confirm your account.'
      );
    }

    // Check for validation errors
    const checkResponse = await fetch(
      `${process.env.REACT_APP_API}/users/emails/${credentials.email}`
    );
    if (checkResponse.status === 400) {
      const errorData = await checkResponse.json();
      throw new Error(errorData.message);
    }

    // Proceed with registration

    // const userData = {
    //   name: credentials.name,
    //   is_admin: false,
    //   district_name: credentials.districtName,
    //   district_id: credentials.districtId,
    //   position: credentials.position,
    // };

    const { data, error } = await supabase.auth.signUp({
      email: credentials.email,
      password: credentials.password,
      options: {
        data: {
          admin: false,
          name: credentials.name,
          district_name: credentials.districtName,
          district_id: credentials.districtId,
          position: credentials.position,
        },
        emailRedirectTo:
          process.env.NODE_ENV === 'development'
            ? `http://localhost:3000/dashboard`
            : `${window.location.origin}/dashboard`,
      },
    });

    if (error) {
      if (error.message.includes('Database error saving new user')) {
        throw new Error('Please enter a valid District ISD Email Address.');
      } else {
        throw error;
      }
    } else {
      try {
        if (!data.user?.id) {
          throw new Error('User ID not found in Supabase response');
        }

        const registerRequest: IRegisterRequest = {
          supabase_id: data.user.id,
          name: credentials.name,
          email: credentials.email,
          role: 'user',
          district_name: credentials.districtName,
          district_id: credentials.districtId,
          position: credentials.position,
        };

        const response = await fetch(`${process.env.REACT_APP_API}/users`, {
          method: 'POST',
          headers: {
            ...getToken(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(registerRequest),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));

          // If this is a duplicate key error and we detected an expired account
          if (
            isExpired &&
            (errorData.message?.includes('E11000') || errorData.message?.includes('duplicate'))
          ) {
            // The cleanup didn't work properly, but since the account was expired,
            // we can proceed as if registration was successful since Supabase signup worked
            localStorage.setItem('role', 'user');
            return;
          }

          console.error('Create user API error:', errorData);
          throw new Error(`Failed to create user in database: ${response.status}`);
        }

        const res = await response.json();
        if (res.statusCode === 201) {
          localStorage.setItem('role', res.data.role);
        }
      } catch (error) {
        console.error('Registration error:', error);
        // If MongoDB creation fails, it means user already exists
        throw new Error(
          'An account with this email already exists. Login or Reset Password if you forgot your credentials.'
        );
      }
    }
  }

  /**
   * Reset password
   */
  async resetPassword(email: string): Promise<void> {
    // Check for expired account first using the existing method
    const { isExpired } = await this.checkAndCleanExpiredAccount(email);
    if (isExpired) {
      throw new Error("Your account wasn't verified within 24 hours. Please register again.");
    }

    // Check MongoDB
    const mongoResponse = await fetch(`${process.env.REACT_APP_API}/users/emails/${email}`);
    if (!mongoResponse.ok) {
      throw new Error(
        'This email address is not registered. Please enter a valid School District email'
      );
    }

    // Check if user is unverified in Supabase
    const admin = this.ensureSupabaseAdmin();
    const {
      data: { users },
      error,
    } = await admin.auth.admin.listUsers({ page: 1, perPage: 1000 });
    if (!error) {
      const supabaseUser = users.find((user) => user.email === email);
      if (supabaseUser && !supabaseUser.email_confirmed_at) {
        throw new Error(
          'Your account is not verified yet. Please check your inbox for the verification email and confirm your account'
        );
      }
    }

    const result = await mongoResponse.json();
    const userData = result.data;

    const redirectUrl =
      userData?.role === 'admin'
        ? process.env.NODE_ENV === 'development'
          ? 'http://localhost:3000/admin/reset-password'
          : `${process.env.REACT_APP_BASE_URL || window.location.origin}/admin/reset-password`
        : process.env.NODE_ENV === 'development'
          ? 'http://localhost:3000/reset-password'
          : `${process.env.REACT_APP_BASE_URL || window.location.origin}/reset-password`;

    localStorage.setItem('role', userData.role);

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });

    if (resetError) throw resetError;
  }

  /**
   * Subscribe to auth state changes
   */
  subscribeToAuthChanges(callback: (event: string, session: Session | null) => void) {
    const { data } = supabase.auth.onAuthStateChange(callback);
    return data.subscription;
  }

  /**
   * Resend verification email for unverified users
   */
  async resendVerificationEmail(email: string): Promise<void> {
    try {
      // Check for expired account first
      const { isExpired } = await this.checkAndCleanExpiredAccount(email);
      if (isExpired) {
        throw new Error("Your account wasn't verified within 24 hours. Please register again.");
      }

      // Check if user exists and is unverified
      const admin = this.ensureSupabaseAdmin();
      const {
        data: { users },
        error: userError,
      } = await admin.auth.admin.listUsers({ page: 1, perPage: 1000 });

      if (userError) {
        throw new Error('Failed to check user status');
      }

      const supabaseUser = users.find((user) => user.email === email);

      if (!supabaseUser) {
        throw new Error(
          'This email address is not registered. Please enter a valid School District email'
        );
      }

      if (supabaseUser.email_confirmed_at) {
        throw new Error('This account is already verified. Please login instead.');
      }

      // Resend verification email
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo:
            process.env.NODE_ENV === 'development'
              ? `http://localhost:3000/dashboard`
              : `${window.location.origin}/dashboard`,
        },
      });

      if (resendError) {
        throw new Error('Failed to resend verification email. Please try again.');
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to resend verification email');
    }
  }

  /**
   * Signs out the current user from Supabase
   * @returns Promise that resolves when the sign out is complete
   */
  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut({ scope: 'global' });

    if (error) throw error;

    // You can also remove the token from localStorage here if needed
    localStorage.removeItem(process.env.REACT_APP_TOKEN_NAME as string);
  }
}

export const AuthServiceApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllowedDomains: builder.query<IAllowedDomainsResponseWrapper, void>({
      query: () => ({
        url: '/file-data/user-lookup',
        method: 'GET',
        headers: getToken(),
      }),
    }),

    createUser: builder.mutation<void, IRegisterRequest>({
      query: (data) => ({
        url: '/users',
        method: 'POST',
        headers: getToken(),
        body: data,
      }),
    }),
  }),
});

export const authService = new AuthService();
export const { useGetAllowedDomainsQuery } = AuthServiceApi;
